import Grid from '@mui/material/Grid';
import { errorMessage } from 'constants/CommonConstants';
import { formRequiredRules } from 'helper';
import React, { useImperativeHandle } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CommonNumberInput } from 'sharedComponents/inputGroup/CommonNumberInput';
import { InputRadio } from 'sharedComponents/inputGroup/InputRadio';
import { RENT_OR_OWN } from '../LeadGenConstant';
import { Lead } from '../LeadGenInterface';
import { postLeadCreation } from 'store/LeadCreationState';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { useDispatch, useSelector } from 'react-redux';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';

const RentOrOwn: React.ForwardRefRenderFunction<unknown, StepperFromProps> = (
  { onNext, onBack, isSubmit, mappedData },
  ref
) => {
  // Hooks
  const { t } = useTranslation();

  const hasCalledNextStep = React.useRef(false);

  // Redux
  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const { authenticateClientData } = useSelector((state: RootState) => state.auth) || {};
  const widgetData = authenticateClientData?.data?.widgetData || {};

  // React Hook Form
  const { control, trigger, watch, getValues } = useFormContext();

  // React Hook Form Watcher
  const rentOrOwnWatcher = watch('rentOrOwn');

  // React Hook Form Trigger
  const nextStep = () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    const { rentOrOwn, monthlyHousePayment } = getValues();

    const rentOrOwnData: Lead = {
      leadSourceId: widgetData?.leadSourceId,
      isSubmit,
      leadDetails: {
        'Rent Or Own': rentOrOwn,
        'Monthly House Payment': parseInt(monthlyHousePayment),
        ...mappedData,
      },
    };

    dispatch(postLeadCreation({ data: rentOrOwnData, leadTypeID: widgetData?.leadTypeId })).then((res) => {
      if (
        res?.payload?.success &&
        (res?.payload?.data?.draftLeadId || res?.payload?.data?._id || res?.payload?.data?.Lead?.leadId)
      ) {
        onNext?.();
      }
    });
  };

  const backStep = () => {
    onBack?.();
  };
  useImperativeHandle(ref, () => ({
    nextStep,
    backStep,
  }));

  return (
    <Grid container justifyContent={{ xs: 'center' }}>
      <Grid item xs={12} lg={9}>
        <h3 className='dy-stepper-form__body__title text-center'>{t('leadGen.rentTxt')}</h3>
        <InputRadio
          rules={formRequiredRules(errorMessage(t).radioError)}
          name={'rentOrOwn'}
          control={control}
          options={RENT_OR_OWN(t)}
          directionRow={false}
          itemPerRow={{
            xs: 12,
            sm: 6,
          }}
          radioVariant='outline'
          triggerValidationForField={(name: string) => {
            trigger(name);
          }}
        />
        {rentOrOwnWatcher && (
          <Grid container justifyContent={{ xs: 'center' }} mt={5}>
            <Grid item xs={12} sm={8}>
              <h3 className='dy-stepper-form__body__title text-center'>{t('leadGen.monthlyHousePayment')}</h3>
              <CommonNumberInput
                name='monthlyHousePayment'
                control={control}
                label={t('leadGen.rentMortgageAmount')}
                placeholder={t('leadGen.rentMortgageAmount')}
                rules={{
                  ...formRequiredRules(errorMessage(t).monthlyPaymentRequired),
                }}
                triggerValidationForField={trigger}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const RentOrOwnStep = React.forwardRef(RentOrOwn);

export default RentOrOwnStep;
